<template lang='pug'>
  header#header.bionic-header(v-if='user')
    v-navigation-drawer.drawer(app fixed left clipped v-model='showDrawer' width='300' :class='{"drawer-background": !$vuetify.theme.dark}')
      div.d-flex.flex-column.fill-height
        v-list(nav dense)
          v-list-item#profile(to='/profile' v-if='user')
            v-list-item-action
              v-icon account_circle
            v-list-item-content
              v-list-item-title {{user.firstName}} {{user.lastName}}
              v-list-item-subtitle {{user.email}}
          v-list-item#account(v-if='user.accountAdmin' to='/account')
            v-list-item-action
              v-icon settings
            v-list-item-content
              v-list-item-title Account
              v-list-item-subtitle(v-if='account') {{account.name}}
          v-list-item#summary(to='/')
            v-list-item-action
              v-icon pie_chart
            v-list-item-title Summary
          v-list-item#dashboard(:to='dashboardUrl')
            v-list-item-action
              v-icon dashboard
            v-list-item-title Dashboard
          v-list-item#testsuites(to='/testsuites')
            v-list-item-action
              v-icon view_module
            v-list-item-title Suites
          v-list-item#tests(to='/tests')
            v-list-item-action
              v-icon view_list
            v-list-item-title Tests
          v-list-item#results(to='/results')
            v-list-item-action
              v-icon done_all
            v-list-item-title Results
          v-list-item#modules(to='/modules')
            v-list-item-action
              v-icon code
            v-list-item-title Modules
          v-list-item#loadTesting(to='/load-tests' v-if='hasLoadTesting')
            v-list-item-action
              v-icon show_chart
            v-list-item-title Load Tests
          v-list-item#documentation(href='https://docs.bionicmetrics.com' target='_blank')
            v-list-item-action
              v-icon library_books
            v-list-item-title Documentation
          v-divider(v-if='hasSuper')
          v-list-group#accounts(v-if='hasSuper' prepend-icon='construction' no-action @click='go("/super")' v-model='showSuper')
            template(v-slot:activator)
              v-list-item-content
                v-list-item-title Super Admin
            v-list-item(to='/super/accounts')
              v-list-item-content
                v-list-item-subtitle Accounts
            v-list-item(to='/super/users')
              v-list-item-content
                v-list-item-subtitle Users
            v-list-item(to='/super/tests')
              v-list-item-content
                v-list-item-subtitle Tests
            v-list-item(to='/super/suites')
              v-list-item-content
                v-list-item-subtitle Suites
            v-list-item(to='/super/transients')
              v-list-item-content
                v-list-item-subtitle Transients
            v-list-item(to='/super/agents')
              v-list-item-content
                v-list-item-subtitle Agents
        div.mt-auto
          v-row(align='end' justify='center')
            // Done this way because a dynamic prop for src doesn't optimize the image
            v-img(v-if='!$vuetify.theme.dark' src='../assets/bionicmetrics-light.png' max-width='200')
            v-img(v-if='$vuetify.theme.dark' src='../assets/bionicmetrics.png' max-width='200')
          v-spacer
          v-list
            v-divider
            v-list-item
              v-row(align='end' justify='space-around')
                v-btn(text small href='https://bionicmetrics.com/terms') Terms
                v-btn(icon small @click='toggleTheme')
                  v-icon(small) {{themeToggleIcon}}
                v-btn(text small href='https://bionicmetrics.com/privacy') Privacy
    v-app-bar.bionic-toolbar(app dark fixed clipped-left :color='barColor' :flat='isFlat')
      v-app-bar-nav-icon(@click='toggleSidenav')
      img.small-logo.hidden-sm-and-up(src='../assets/simple.png' @click="go('/')" v-if='!breadcrumbs')
      img.small-logo(src='../assets/simple.png' @click="go('/')" v-if='breadcrumbs')
      .hidden-sm-and-up.small-title {{title}}
      v-breadcrumbs#breadcrumbs.hidden-xs-only(large dark :items='breadcrumbs' v-if='breadcrumbs')
        template(v-slot:divider)
          v-icon chevron_right
      v-spacer
      img.header-logo.hidden-xs-only(src='../assets/bionicmetrics-short.png' @click="go('/')" v-if='!breadcrumbs')
      v-spacer
      bionic-metrics-saving(:saving-status='savingStatus')
      bionic-metrics-git-branch
      v-btn#exitAssumeAccount(v-if='isAssumedAccount' color='error' @click='unassumeAccount').ml-2 Exit ({{account.name}})
      v-btn#exitAssumeUser(v-if='isAssumedUser' color='error' @click='unassumeUser') Exit ({{user.email}})
      v-menu(offset-y left)
        template(v-slot:activator='{on}')
          v-btn#menu(text icon v-on='on')
            v-icon more_vert
        v-list
          v-list-item#menu-profile(to='/profile')
            v-list-item-action
              v-icon account_circle
            v-list-item-title
              | Profile
          v-list-item#menu-account(v-if='user.accountAdmin' to='/account')
            v-list-item-action
              v-icon settings
            v-list-item-title
              | Account
          v-list-item#menu-users(v-if='user.accountAdmin' to='/account#users')
            v-list-item-action
              v-icon supervised_user_circle
            v-list-item-title
              | Users
          v-list-item#menu-integrations(v-if='user.accountAdmin && hasIntegrations' to='/integrations')
            v-list-item-action
              v-icon apps
            v-list-item-title
              | Integrations
          v-list-item#signout(@click='signout')
            v-list-item-action
              v-icon exit_to_app
            v-list-item-title
              | Sign Out

</template>
<script>
import {get} from 'lodash';
import {mapGetters, mapActions} from 'vuex';
import Events from '@/services/events.service';
import Notifications from '@/services/notification.service';
import BionicMetricsSaving from '@/components/Saving';
import BionicMetricsGitBranch from '@/components/GitBranch';

export default {
  components: {BionicMetricsGitBranch, BionicMetricsSaving},
  name: 'BionicMetricsHeader',
  computed: {
    isFlat() {
      if (this.$vuetify.theme.dark) return true;
      return this.hasSubheader && (this.$vuetify.breakpoint.mdAndDown || !this.showDrawer);
    },
    hasSuper() {
      return this.isSuperAdmin && !this.isAssumedAccount && !this.isAssumedUser;
    },
    isSuperAdmin() {
      return get(this.user, 'superAdmin');
    },
    hasIntegrations() {
      return get(this.account, 'entitlements.hasIntegrations') || this.isSuperAdmin;
    },
    hasLoadTesting() {
      return get(this.account, 'entitlements.hasLoadTesting') || this.isSuperAdmin;
    },
    dashboardUrl() {
      if (!this.gitMode) return '/dashboard/primary';
      return `/dashboard/${this.gitBranch}`;
    },
    barColor() {
      if (!this.$vuetify.theme.dark) return 'primary';
      return 'default';
    },
    dark() {
      return this.$vuetify.theme.dark;
    },
    themeToggleIcon() {
      if (this.dark) return 'light_mode';
      return 'nightlight_round';
    },
    ...mapGetters({
      user: 'getUser',
      account: 'getAccount',
      gitMode: 'getGitMode',
      gitBranch: 'getGitBranch',
      title: 'getTitle',
      hasSubheader: 'getHasSubheader',
      isAssumedAccount: 'isAssumedAccount',
      isAssumedUser: 'isAssumedUser',
      breadcrumbs: 'getBreadcrumbs',
      savingStatus: 'getSavingStatus'
    })
  },
  async mounted() {
    // Show the drawer if we're on larger screens by default
    this.showDrawer = this.$vuetify.breakpoint.lgAndUp;
  },
  methods: {
    async signout() {
      Events.$emit('signout');
      await this.peaceOut();
      this.$vuetify.theme.dark = false;
      this.go('/signin');
    },
    async unassumeAccount() {
      await this.userUnassumeAccount();
      this.go('/super/accounts');
    },
    async unassumeUser() {
      await this.userUnassumeUser();
      this.go('/super/users');
    },
    go(to) {
      this.$router.push(to);
    },
    toggleSidenav() {
      this.showDrawer = !this.showDrawer;
    },
    closeSidenav() {
      this.showDrawer = false;
    },
    async toggleTheme() {
      const isDark = !this.dark;
      this.$vuetify.theme.dark = isDark;
      try {
        await this.userSetTheme(isDark);
      } catch (error) {
        console.error('Unable to save theme:');
        console.error(error);
        Notifications.errorToast('Error saving theme settings', {duration: 5000});
      }
    },
    ...mapActions({
      peaceOut: 'signout',
      userUnassumeAccount: 'userUnassumeAccount',
      userUnassumeUser: 'userUnassumeUser',
      userSetTheme: 'userSetTheme'
    })
  },
  data() {
    return {
      showDrawer: false,
      showSuper: true
    };
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
.v-toolbar--clipped {
  z-index: 5;
}

.header-logo {
  max-height: 65px;
  margin-left: 10px;
}

.header-logo {
  max-height: 38px;
  min-height: 38px;
  margin-left: 10px;
}

.small-logo {
  max-height: 25px;
  min-height: 25px;
  height: 25px;
}

.small-title {
  margin-left: 10px;
}

#breadcrumbs > li > a {
  color: white;
}

.tos-item {
  a:link {
    text-decoration: none;
  }

  a:visited {
    text-decoration: none;
  }

  a:hover {
    color: #546E7A;
    text-decoration: underline;
  }

  a:active {
    color: #546E7A;
    text-decoration: underline;
  }
}

.bottom-part {
  margin-top: auto;
}

.drawer {
  z-index: 10;
}

.drawer-background {
  background-image: url("../../public/img/background.png");
  background-size: cover;
}
</style>
